/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-param-reassign */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format, formatDistanceStrict, isBefore } from 'date-fns';
import moment from 'moment';
import { IoIosArrowBack } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Input, InputDate, Layout, Select, Textarea } from '~/components';
import { useScreen } from '~/hooks/screen';
import api from '~/services/api';
import {
  currencyFormatter,
  currencyToNumberFormatter,
} from '~/utils/formatter';
import getValidationErrors from '~/utils/getValidationErrors';
import { regex_remove_all_non_digits } from '~/utils/regex';

import {
  Container,
  PageHeader,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  PageHeaderSaveButton,
  FieldSet,
  FormLabel,
  RadioInput,
  SelectContainer,
} from './styles';

type RouteParams = {
  userId: string;
};

export type Package = {
  id: number;
  professional_id: number;
  gym_id: number;
  name: string;
  lealtad: string;
  status: string;
  value: number;
  number_of_training_programs: number;
  created_at: string;
  updated_at: string;
};

export type PackageOption = {
  value: string;
  label: string;
};

type FormProps = {
  package_id: number;
  start_date: string;
  end_date: string;
  expiration_day: string;
  payment_form: string;
  monthly_amount: string;
  total_amount: string;
  status: string;
  training_programs: string;
};

const BillingRegister: React.FC = () => {
  const { goBack } = useHistory();
  const { userId } = useParams<RouteParams>();
  const { updateScreen } = useScreen();

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<number>();
  const [packagesListOptions, setPackagesListOptions] = useState<
    PackageOption[]
  >([]);
  const [startDate, setStartDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd'),
  );

  // Radio select de formas de pagamento
  const [fidelizationWay, setFidelizationWay] = useState('');
  const [selectedPayWay, setSelectedPayWay] = useState('billet');

  useEffect(() => {
    updateScreen(`billing`);
  }, [updateScreen]);

  // Pegar os pacotes da api, filtra os ativos e cria a lista do select de pacotes
  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get('packages');

      const filteredPackages = data.data?.filter(
        (_package: Package) => _package.status === 'active',
      );

      setPackages(filteredPackages);

      const mappedPackages = filteredPackages.map((_package: Package) => ({
        value: Number(_package.id),
        label: _package.name,
      }));

      setPackagesListOptions(mappedPackages);
    }

    fetchData();

    setLoading(false);
  }, []);

  // Pegar o valor mensal do pacote selecionado
  const setMonthlyAmount = useCallback(
    (id: number | string) => {
      const selectedPackage = packages.filter(
        (_package: Package) => id === _package.id,
      )[0];

      formRef.current?.setFieldValue(
        'monthly_amount',
        currencyFormatter(selectedPackage?.value),
      );
    },
    [packages],
  );

  const fidelizations = useMemo(
    () => [
      { id: 'monthly', label: 'Mensal', value: 1 },
      { id: 'quarterly', label: 'Trimestral', value: 3 },
      { id: 'semiannual', label: 'Semestral', value: 6 },
      { id: 'yearly', label: 'Anual', value: 12 },
    ],
    [],
  );

  const payWayVariations = useMemo(
    () => [
      { id: 'billet', label: 'Boleto' },
      { id: 'cash', label: 'Dinheiro' },
      { id: 'credit_card', label: 'Cartão de crédito' },
      { id: 'debit_card', label: 'Cartão de débito' },
      { id: 'transfer', label: 'Transferência' },
      { id: 'others', label: 'Outros' },
    ],
    [],
  );

  // Calculo para dar o valor total
  useEffect(() => {
    const selectedPackage = packages.filter(
      (_package: Package) => selectedPackageId === _package.id,
    )[0];

    try {
      if (Object.keys(selectedPackage).length > 0) {
        const fidelizationValue = fidelizations.find(
          (data) => data.id === selectedPackage?.lealtad,
        )?.value;

        const endDateWithMonths = moment(startDate)
          .add(fidelizationValue, 'months')
          .format('YYYY-MM-DD')
          .toString();

        const mothDistance = formatDistanceStrict(
          new Date(startDate),
          new Date(endDateWithMonths),
          { unit: 'month' },
        ).replace(regex_remove_all_non_digits, '');

        formRef.current?.setFieldValue(
          'total_amount',
          currencyFormatter(selectedPackage?.value * (+mothDistance || 1)),
        );

        formRef.current?.setFieldValue(
          'training_programs',
          selectedPackage?.number_of_training_programs,
        );

        formRef.current?.setFieldValue('end_date', endDateWithMonths);

        setEndDate(endDateWithMonths);

        setFidelizationWay(selectedPackage.lealtad);
      }
    } catch (err) {
      //
    }
  }, [selectedPackageId, packages, startDate]);

  // Lista do select de status da fatura
  const financialStatusOptions = useMemo(
    () => [
      { value: 'pending', label: 'Pendente' },
      { value: 'paid', label: 'Paga' },
      { value: 'overdue', label: 'Vencida' },
      { value: 'canceled', label: 'Cancelada' },
    ],
    [],
  );

  const FidelizationOptions = useMemo(
    () =>
      fidelizations.map((item) => (
        <RadioInput key={item.label} isSelected={item.id === fidelizationWay}>
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioInput>
      )),
    [fidelizations, fidelizationWay],
  );

  const RadioStatusOptions = useMemo(
    () =>
      payWayVariations.map((item) => (
        <RadioInput
          key={item.label}
          isSelected={item.id === selectedPayWay}
          onClick={() => setSelectedPayWay(item.id)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioInput>
      )),
    [payWayVariations, selectedPayWay],
  );

  // submit  do formulário
  const handleSubmit = useCallback(
    async (data: FormProps) => {
      setLoading(true);

      // @ts-expect-error
      data?.training_programs && delete data?.training_programs;

      const schema = Yup.object().shape({
        student_id: Yup.number().required('O campo é obrigatório!'),
        package_id: Yup.number().required('O campo é obrigatório!'),
        start_date: Yup.string().required('O campo é obrigatório!'),
        end_date: Yup.string().required('O campo é obrigatório!'),
        expiration_day: Yup.string().required('O campo é obrigatório!'),
        payment_form: Yup.string().required('O campo é obrigatório!'),
        monthly_amount: Yup.number().required('O campo é obrigatório!'),
        total_amount: Yup.number().required('O campo é obrigatório!'),
        status: Yup.string().required('O campo é obrigatório!'),
        observations: Yup.string(),
      });

      const formData = {
        ...data,
        student_id: +userId,
        monthly_amount: currencyToNumberFormatter(data.monthly_amount),
        total_amount: currencyToNumberFormatter(data.total_amount),
        payment_form: selectedPayWay,
      };

      const IsInitialDateBeforeEndDate = isBefore(
        new Date(formData.end_date),
        new Date(formData.start_date),
      );

      if (IsInitialDateBeforeEndDate) {
        setLoading(false);

        toast.error(
          'Erro ao cadastrar fatura!\nA data de termino não pode ser anterior a data de início!',
        );

        return;
      }

      try {
        await schema.validate(formData, { abortEarly: false });

        await api.post('billings', formData);

        toast.success('Fatura cadastrada com sucesso!');

        goBack();
      } catch (err) {
        console.log(err);
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        toast.error('Erro ao cadastrar fatura!');
      }
    },
    [userId, selectedPayWay, goBack],
  );

  const inputStyle = useMemo(
    () => ({
      height: 35,
      padding: '0 1.7rem',
    }),
    [],
  );

  const textAreaStyle = useMemo(
    () => ({
      width: '100%',
      marginTop: 5,

      height: '19rem',
      padding: '2rem',
      borderRadius: '1rem',
    }),
    [],
  );

  return (
    <Layout title="Aluno">
      <PageHeader>
        <PageHeaderBackButton onClick={() => goBack()}>
          <IoIosArrowBack />
          <PageHeaderBackButtonLabel>nova fatura</PageHeaderBackButtonLabel>
        </PageHeaderBackButton>

        <PageHeaderSaveButton
          disabled={loading}
          onClick={() => formRef.current?.submitForm()}
        >
          Salvar
        </PageHeaderSaveButton>
      </PageHeader>

      <Container>
        {!loading && (
          <Form ref={formRef} onSubmit={handleSubmit}>
            <div>
              <FieldSet>
                <FormLabel>Pacote:</FormLabel>
                <SelectContainer>
                  <Select
                    name="package_id"
                    isSearchable={false}
                    placeholder="Selecionar"
                    options={packagesListOptions}
                    onChange={(e) => {
                      setMonthlyAmount(e?.value);
                      setSelectedPackageId(e?.value);
                    }}
                    styles={{
                      placeholder: () => ({
                        padding: '0 1rem',
                        color: '#00000080',
                      }),
                      singleValue: () => ({
                        padding: '0 1rem',
                      }),
                    }}
                  />
                </SelectContainer>
              </FieldSet>

              <FieldSet>
                <FormLabel>Fidelização:</FormLabel>
                {FidelizationOptions}
              </FieldSet>

              <FieldSet>
                <FormLabel>Programas de treino:</FormLabel>
                <Input
                  name="training_programs"
                  placeholder="Programas de treino"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Valor mensal:</FormLabel>
                <Input
                  name="monthly_amount"
                  placeholder="R$0,00"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Valor total:</FormLabel>
                <Input
                  name="total_amount"
                  placeholder="R$0,00"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Data de início:</FormLabel>
                <InputDate
                  name="start_date"
                  defaultValue={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Data de início"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Data de término:</FormLabel>
                <InputDate
                  name="end_date"
                  placeholder="Data de término"
                  onChange={(e) => setEndDate(e.target.value)}
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                  disabled
                />
              </FieldSet>
            </div>

            <div>
              <FieldSet>
                <FormLabel>Dia de vencimento:</FormLabel>
                <Input
                  name="expiration_day"
                  defaultValue={1}
                  placeholder="Dia do vencimento"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Meio de pagamento:</FormLabel>
                {RadioStatusOptions}
              </FieldSet>

              <FieldSet>
                <FormLabel>Status financeiro:</FormLabel>

                <SelectContainer>
                  <Select
                    name="status"
                    isSearchable={false}
                    placeholder="Selecionar"
                    defaultValue={financialStatusOptions[0]}
                    options={financialStatusOptions}
                    styles={{
                      placeholder: () => ({
                        padding: '0 1rem',
                        color: '#00000080',
                      }),
                      singleValue: () => ({
                        padding: '0 1rem',
                      }),
                    }}
                  />
                </SelectContainer>
              </FieldSet>

              <FieldSet>
                <FormLabel>Dados adicionais:</FormLabel>
                <Textarea
                  name="observations"
                  containerStyle={textAreaStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>
            </div>
          </Form>
        )}
      </Container>
    </Layout>
  );
};

export default BillingRegister;
